<template>
  <CreateModal @closeCreateModal="resetForm" ref="createModal" type="customer">
    <h-loading v-if="!realEstate.id && isLoading" />

    <v-form class="mw-650 mx-auto" ref="realEstateForm" v-model="valid">
      <h3 class="mb-5">{{ $t('language.location') }}</h3>

      <v-row>
        <v-col cols="12" sm="4" class="pb-0">
          <HCountrySelect
            v-model="realEstate.country"
            :country="realEstate.country"
            :disabled="isEditing"
          />
        </v-col>
        <v-col cols="6" sm="5" class="pb-0">
          <HLanguageSelect
            v-model="realEstate.language"
            :country="realEstate.country"
          />
        </v-col>
        <v-col cols="6" sm="3" class="pb-0">
          <HCurrencySelect
            v-model="realEstate.currency"
            :country="realEstate.country"
            :disabled="true"
          />
        </v-col>
      </v-row>

      <v-divider class="mb-10" />

      <h3 class="mb-5">{{ $t('customer.label.details') }}</h3>

      <v-text-field
        v-model="realEstate.name"
        :label="$t('customer.create.name')"
        outlined
      />

      <HDocumentID
        v-model="realEstate.cnpj"
        :default-value="realEstate.cnpj"
        :rules="verifyDocument"
        :country="realEstate.country"
      />

      <AddressSearch
        v-if="!realEstate.id"
        @address="getAddressData"
        :getFocus="false"
        :rules="required"
        :selectedCountry="realEstate.country"
      />

      <v-text-field
        v-if="!realEstate.id"
        v-model="city"
        :label="$t('customer.create.city')"
        outlined
        disabled
      />

      <v-text-field
        v-if="!realEstate.id"
        v-model="realEstate.email"
        :label="$t('customer.create.email')"
        :rules="emailRule"
        outlined
      />

      <HPhone
        v-if="!realEstate.id"
        v-model="realEstate.phone"
        :default-value="realEstate.phone"
        :rules="required"
        :country="realEstate.country"
      />

      <v-text-field
        v-if="!realEstate.id"
        v-model="realEstate.appointmentResumeContact"
        :label="$t('customer.create.appointmentResumeContact')"
        :rules="[emailCheckPassEmpty]"
        outlined
      />

      <v-textarea
        :label="$t('customer.create.informations')"
        v-model="realEstate.information"
        hide-details
        outlined
        rows="3"
      />

      <v-divider class="my-10" />

      <h3 class="mb-5">{{ $t('customer.label.monetary') }}</h3>
      <HAmount
        v-model="realEstate.negativeBalanceLimit"
        :amount="realEstate.negativeBalanceLimit"
        :label="$t('customer.create.negativeBalanceLimit')"
        :minAmount="-9999999"
      />

      <v-divider class="my-10" />

      <h3 class="mb-5">{{ $t('customer.create.logo') }}</h3>
      <p v-show="customerLogo" class="customerLogoWrapper text-center lighten-4 grey pa-5">
        <img ref="customerLogo" width="200" src="#" />
      </p>

      <HUpload
        @change="handleCustomerLogo"
        fileTypes="image/png, image/gif, image/jpeg, image/jpg"
        small
      />

      <v-divider class="my-10" />

      <span v-if="partner">
        <h3 class="mb-1">{{ $t('host.label.partner') }}</h3>
        <div class="d-flex align-start mb-10">
          <p class="mb-0 mr-2">
            {{ partner.name }}
            <small class="d-block">
              {{ partner.description }}
            </small>
          </p>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                @click="removePartner"
                v-bind="attrs"
                v-on="on"
                color="realEstate"
                class="mt-n1"
                icon
              >
                <v-icon small> mdi mdi-close-circle-outline </v-icon>
              </v-btn>
            </template>
            {{ $t('customer.create.removePartner') }}
          </v-tooltip>
        </div>
      </span>

      <HAutocomplete
        v-else
        v-model="realEstate.partner_id"
        :items="partners"
        :item-text="'name'"
        :item-value="'id'"
        :label="$t('host.label.partner')"
        clearable
      />

      <!-- OPERATION TYPES -->
      <v-divider class="mt-3 mb-10" />

      <h3 class="mb-3">{{ $t('operationTypes.title') }}</h3>

      <v-select
        v-model="realEstate.worker_types"
        :label="$t('operationTypes.selectOperationTypes')"
        :items="workerTypeOptions"
        :item-text="'text'"
        :item-value="'value'"
        :hint="$t('operationTypes.hints.canSelectMultipleOptions')"
        :persistent-hint="true"
        :rules="requiredArray"
        :disabled="isThirdParty"
        class="mb-3"
        outlined
        multiple
        chips
        small-chips
        clearable
      >
        <template v-slot:item="{ item }">
          <span class="d-block py-2">
            <p class="mb-0 body-1">{{ item.text }}</p>
            <p class="mb-0 caption grey--text text--darken-2">{{ item.description }}</p>
          </span>
        </template>
      </v-select>

      <v-select
        v-model="realEstate.internal_daily_service_limit"
        :label="$t('operationTypes.internalDailyServiceLimit')"
        :items="dailyServiceLimits"
        :hint="$t('operationTypes.hints.internalDailyServiceLimit')"
        :persistent-hint="true"
        class="mb-3 mw-300"
        outlined
      />

      <v-switch
        v-if="!realEstate.id"
        v-model="realEstate.sendToNomah"
        :label="$t('customer.create.sendToNomah')"
        inset
      />

      <v-switch
        v-model="realEstate.isTester"
        :label="$t('customer.create.tester')"
        inset
      />

      <!-- TODO: In the future, this will be changed allow multiple worker types.
       Nowadays, it's used to define customers that uses only internal inspectors -->
      <!-- <v-switch
        v-model="realEstate.useCustomWorkerType"
        :label="$t('customer.create.useCustomWorkerType')"
        class="mr-2"
        hide-details
        inset
      /> -->

      <v-divider class="my-10" />

      <h3 class="mb-5">{{ $t('customer.create.feedback') }}</h3>

      <v-switch
        inset
        :label="$t('customer.create.usesCustomFeedback')"
        v-model="realEstate.usesCustomFeedback"
      />

      <v-text-field
        v-show="realEstate.usesCustomFeedback"
        v-model="realEstate.feedbackURL"
        :label="$t('customer.create.feedbackURL')"
        :disabled="!realEstate.usesCustomFeedback"
        outlined
      />

      <v-switch
        inset
        :label="$t('customer.create.sendEmailConfirmationCopy')"
        v-model="realEstate.receiveConfirmationMailCopy"
      />

      <v-switch
        inset
        :label="$t('customer.create.sendEmailToEscort')"
        v-model="realEstate.customer_emails_enabled"
      />

      <v-switch
        inset
        :label="$t('customer.create.useCancelationLimit')"
        v-model="realEstate.use_cancelation_limit"
      />

      <v-switch
        v-model="hcheckSyncEnabled"
        :label="$t('profile.updateRealEstate.label.hcheckSyncEnabled')"
        inset
      />

      <v-switch
        inset
        :label="$t('customer.create.allowKeyPickup')"
        v-model="realEstate.allows_key_pickup"
      />

      <v-switch
        inset
        :label="$t('customer.create.allowsPeriodAppointment')"
        v-model="realEstate.allows_period_appointment"
      />

      <v-switch
        inset
        :disabled="!realEstate.allows_period_appointment"
        :label="$t('customer.create.allowsEndTimeAppointment')"
        v-model="realEstate.allows_end_time_appointment"
      />

      <v-select
        v-if="realEstate.id"
        v-model="realEstate.service_duration_validation_type"
        :label="$t('customer.create.serviceDurationValidationType.label')"
        :items="serviceDurationValidationTypeOptions"
        item-text="text"
        item-value="value"
        class="mw-300"
        outlined
      />

      <v-text-field
        v-model="realEstate.dispute_creation_days_limit"
        :label="$t('customer.create.disputeCreationDaysLimit')"
        :hint="$t('customer.create.disputeCreationDaysLimitHint')"
        :rules="disputeCreationDaysLimitRule"
        :min="minDisputeCreationDaysLimit"
        :max="maxDisputeCreationDaysLimit"
        type="number"
        class="mw-300 mb-3"
        persistent-hint
        outlined
      />

      <v-select
        v-show="realEstate.use_cancelation_limit"
        v-model="realEstate.cancelation_limit_hours"
        :label="$t('customer.create.cancelationLimitHours')"
        :rules="cancelationLimitHoursRule"
        :items="limitHours"
        class="mw-300"
        outlined
      />

      <v-divider class="mb-8 mt-2" />

      <!-- DEVOLUS -->
      <h3 class="mb-5">{{ $t('customer.create.devolus') }}</h3>

      <v-switch
        v-if="realEstate.id"
        v-model="realEstate.devolusEnabled"
        :label="$t('customer.create.devolusEnabled')"
        inset
      />

      <v-text-field
        v-model="realEstate.devolusToken"
        :label="$t('customer.create.devolusToken')"
        outlined
      />

      <v-text-field
        v-model="realEstate.devolusId"
        :label="$t('customer.create.devolusId')"
        outlined
      />

      <v-text-field
        v-model="realEstate.devolusWebhookUrl"
        :label="$t('customer.create.devolusWebhookUrl')"
        outlined
      />
      <!-- /DEVOLUS -->

      <div v-if="!realEstate.id">
        <v-divider class="mb-8 mt-2" />

        <h3 class="mb-5">{{ $t('customer.create.contract') }}</h3>

        <v-switch
          v-model="realEstate.addDefaultContract"
          :label="$t('customer.create.addDefaultContract')"
          inset
        />

        <div v-show="realEstate.addDefaultContract">
          <v-select
            v-model="realEstate.subscriptionType"
            :items="subscriptionTypes"
            :label="$t('customer.create.subscriptionType')"
            :rules="contractRule"
            item-value="value"
            item-text="name"
            class="mt-4"
            outlined
          />

          <RealEstateServiceTypeSelect
            v-model="realEstate.services"
            :disable="!realEstate.addDefaultContract"
            :rules="contractRule"
            class="mb-7"
          />

          <SelectDate
            v-model="realEstate.startDate"
            :label="$t('contract.label.startDate')"
            :rules="contractRule"
            class="mb-7"
          />

          <v-text-field
            v-model="realEstate.monthlyCapacity"
            :label="$t('contract.label.monthlyCapacity')"
            :rules="contractRule"
            type="number"
            outlined
          />

          <v-text-field
            v-model="realEstate.dailyCapacity"
            :label="$t('contract.label.monthlyAmount')"
            :rules="contractRule"
            type="number"
            outlined
          />

          <v-text-field
            v-model="realEstate.overuseFee"
            :label="$t('contract.label.overuseFee')"
            :rules="[optionalNotNegative]"
            :disabled="!realEstate.addDefaultContract"
            type="number"
            outlined
          />
        </div>
      </div>

      <div v-if="realEstate.id">
        <v-divider class="mb-8 mt-2" />

        <AdminBranchSelect
          v-model="realEstate.headquarter"
          :realEstateId="realEstate.id"
          :label="$t('customer.create.headquarter')"
          :rules="required"
        />
      </div>

      <div v-if="!realEstate.id">
        <v-divider class="mb-8 mt-2" />

        <h3 class="mb-5">{{ $t('customer.create.realEstateUser') }}</h3>

        <v-text-field
          v-model="newUser.name"
          :label="$t('customer.create.name')"
          :rules="required"
          outlined
        />

        <HDocumentID
          v-model="newUser.cpf"
          :default-value="newUser.cpf"
          :country="realEstate.country"
          :rules="cpfRule"
          :label="$t('realEstateUsers.label.cpf')"
        />

        <v-text-field
          v-model="newUser.email"
          :label="$t('customer.create.email')"
          :rules="emailRule"
          outlined
        />

        <HPhone
          v-model="newUser.phone"
          :default-value="newUser.phone"
          :rules="required"
          :country="realEstate.country"
        />
      </div>

      <div class="d-flex justify-end mt-5">
        <v-btn
          @click="save"
          :loading="isLoading"
          :disabled="!valid"
          color="primary"
          elevation="0"
          large
        >
          {{ $t('create.save') }}
        </v-btn>
      </div>
    </v-form>
  </CreateModal>
</template>
<script>
import CreateModal from '@/components/app/CreateModal'
import { catalogPhone } from '@/util/catalog'
import {
  cancelationLimitHoursRule,
  cpfRule,
  disputeCreationDaysLimitRule,
  documentRule,
  emailRule,
  phoneRule,
  required,
  requiredArray
} from '@/util/defaultValidationRules'
import { subscriptionTypes, workerTypeOptions, serviceDurationValidationTypeOptions } from '@/util/enums'
import { emailCheckPassEmpty, optionalNotNegative } from '@/util/errorHandling'
import {
  MAX_DISPUTE_CREATION_DAYS_LIMIT,
  MIN_DISPUTE_CREATION_DAYS_LIMIT,
  toDisputeCreationDaysLimitBody,
} from '@/util/disputeCreationDaysLimit'
import { unmaskText } from '@/util/helpers'
import { toOveruseFee } from '@/util/overuseFee'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { required as fieldRequired } from '../../util/errorHandling'

import AddressSearch from '@/components/app/AddressSearch'
import HDocumentID from '@/components/app/HDocumentID'
import HPhone from '@/components/app/HPhone'
import HTextInput from '@/components/app/HTextInput'
import AdminBranchSelect from '@/components/branch/AdminBranchSelect'
import RealEstateServiceTypeSelect from '@/components/realEstate/RealEstateServiceTypeSelect'
import HAmount from '../app/HAmount.vue'
import HAutocomplete from '../app/HAutocomplete.vue'
import HCountrySelect from '../app/HCountrySelect'
import HCurrencySelect from '../app/HCurrencySelect'
import HLanguageSelect from '../app/HLanguageSelect'
import SelectDate from '../date/SelectDate'
import HUpload from '../lib/HUpload.vue'

// O backend usa os mesmos números: nasce em 8 e o teto do parâmetro é 20 (task #1509).
const DEFAULT_INTERNAL_DAILY_SERVICE_LIMIT = 8
const MAX_INTERNAL_DAILY_SERVICE_LIMIT = 20

export default {
  name: 'CustomerCreateUpdate',

  components: {
    CreateModal,
    HTextInput,
    HDocumentID,
    HPhone,
    HUpload,
    HCountrySelect,
    HCurrencySelect,
    HLanguageSelect,
    HAutocomplete,
    AddressSearch,
    RealEstateServiceTypeSelect,
    AdminBranchSelect,
    SelectDate,
    HAmount,
  },

  data() {
    return {
      valid: false,
      city: null,
      formatPhone: catalogPhone,
      subscriptionTypes: subscriptionTypes,
      emailCheckPassEmpty,
      optionalNotNegative,
      customerLogo: null,
      customerLogoThumbnail: null,
      partner: null,
      newUser: {},
      realEstate: {
        country: 'BR',
        language: 'PT_BR',
        currency: 'BRAZILIAN_REAL',
        partner_id: null,
        negativeBalanceLimit: 0,
        worker_types: [],
      },
      hcheckSyncEnabled: true,
      loadedDisputeCreationDaysLimit: null,
    }
  },

  computed: {
    ...mapGetters(['customer', 'isLoading', 'partners', 'isAdmin']),

    required() {
      return required
    },

    requiredArray() {
      return requiredArray
    },

    verifyDocument() {
      return documentRule
    },

    emailRule() {
      return this.required.concat(emailRule)
    },

    phoneRule() {
      return this.required.concat(phoneRule)
    },

    cpfRule() {
      return cpfRule
    },

    contractRule() {
      if (!this.realEstate.addDefaultContract) return []
      return [fieldRequired]
    },

    cancelationLimitHoursRule() {
      return cancelationLimitHoursRule
    },

    disputeCreationDaysLimitRule() {
      return disputeCreationDaysLimitRule
    },

    minDisputeCreationDaysLimit() {
      return MIN_DISPUTE_CREATION_DAYS_LIMIT
    },

    maxDisputeCreationDaysLimit() {
      return MAX_DISPUTE_CREATION_DAYS_LIMIT
    },

    limitHours() {
      const hours = []

      for (let i = 1; i < 49; i++) {
        const hour = {
          text: `${i} ${
            i === 1
              ? this.$t('customer.create.hour')
              : this.$t('customer.create.hours')
          }`,
          value: i,
        }

        hours.push(hour)
      }

      return hours
    },

    dailyServiceLimits() {
      return Array.from({ length: MAX_INTERNAL_DAILY_SERVICE_LIMIT }, (_, index) => index + 1)
    },

    isEditing() {
      return this.realEstate.id ? true : false
    },

    isThirdParty() {
      const types = this.realEstate.worker_types || []
      return types.includes('THIRD_PARTY')
    },

    workerTypeOptions() {
      return workerTypeOptions
    },

    serviceDurationValidationTypeOptions() {
      return serviceDurationValidationTypeOptions
    },
  },

  methods: {
    ...mapActions([
      'createCustomer',
      'updateCustomer',
      'fetchCustomer',
      'showSuccessMessage',
      'showErrorMessage',
      'fetchCustomers',
      'fetchPartners',
    ]),

    ...mapMutations(['setIsLoading']),

    async openUpdateModal(realEstateId) {
      this.setIsLoading(true)
      this.$refs.createModal.dialog = true

      await this.fetchCustomer(realEstateId)
        .then(() => {
          this.realEstate = {
            id: this.customer.id,
            cnpj: this.customer.cnpj.replace(/\D+/g, ''),
            email: this.customer.email,
            name: this.customer.name,
            currency: this.customer?.currency,
            phone: this.customer.phone,
            email: this.customer.email,
            information: this.customer.information,
            isTester: this.customer.is_tester,
            usesCustomFeedback: this.customer.uses_custom_feedback,
            use_cancelation_limit: this.customer.use_cancelation_limit,
            cancelation_limit_hours: this.customer.cancelation_limit_hours,
            feedbackURL: this.customer.custom_feedback_url,
            address: this.customer.address,
            postalCode: this.customer.postalCode,
            appointmentResumeContact: this.customer.appointment_resume_contact,
            devolusEnabled: this.customer.devolus_enabled,
            devolusToken: this.customer.devolus_token,
            devolusId: this.customer.devolus_id,
            devolusWebhookUrl: this.customer.devolus_webhook_url,
            headquarter: this.customer.headquarter,
            receiveConfirmationMailCopy:
              this.customer.receive_confirmation_mail_copy,
            customer_emails_enabled: this.customer.customer_emails_enabled,
            language: this.customer?.language,
            country: this.customer?.country,
            allows_key_pickup: this.customer?.allows_key_pickup,
            partner_id: this.customer?.partner?.id,
            negativeBalanceLimit: this.customer?.negative_balance_limit,
            useCustomWorkerType: this.customer?.use_custom_worker_type,
            worker_types: this.customer?.worker_types || this.customer?.workerTypes || [],
            allows_period_appointment: this.customer?.allows_period_appointment,
            allows_end_time_appointment: this.customer?.allows_end_time_appointment,
            service_duration_validation_type: this.customer?.service_duration_validation_type,
            internal_daily_service_limit:
              this.customer?.internal_daily_service_limit ??
              DEFAULT_INTERNAL_DAILY_SERVICE_LIMIT,
            dispute_creation_days_limit: this.customer?.dispute_creation_days_limit ?? null,
          }

          this.loadedDisputeCreationDaysLimit = this.realEstate.dispute_creation_days_limit

          this.hcheckSyncEnabled = this.customer.hcheck_sync_enabled ?? false
          this.partner = this.customer.partner
          this.customerLogo = this.customer.logo_url
          this.setCustomerLogo()
        })
        .finally(() => this.setIsLoading(false))
    },

    getAddressData(data) {
      this.realEstate.address = data.address
      this.realEstate.postalCode = data.postalCode
      this.city = data.city
    },

    buildUpdateBody() {
      var realEstate = {
        id: this.realEstate.id,
        name: this.realEstate.name,
        cnpj: unmaskText(this.realEstate.cnpj),
        currency: this.realEstate.currency,
        information: this.realEstate.information,
        uses_custom_feedback: this.realEstate.usesCustomFeedback,
        use_cancelation_limit: this.realEstate.use_cancelation_limit,
        cancelation_limit_hours: this.realEstate.cancelation_limit_hours,
        is_tester: this.realEstate.isTester,
        devolus_token: this.realEstate.devolusToken,
        devolus_id: this.realEstate.devolusId,
        devolus_webhook_url: this.realEstate.devolusWebhookUrl,
        devolus_enabled: this.realEstate.devolusEnabled,
        headquarter_id: this.realEstate.headquarter.id,
        receive_confirmation_mail_copy:
          this.realEstate.receiveConfirmationMailCopy,
        customer_emails_enabled: this.realEstate.customer_emails_enabled,
        language: this.realEstate.language,
        country: this.realEstate.country,
        allows_key_pickup: this.realEstate.allows_key_pickup,
        partner_id: this.realEstate.partner_id,
        logo: this.customerLogo,
        negative_balance_limit: this.realEstate.negativeBalanceLimit ?? 0,
        use_custom_worker_type: this.realEstate.useCustomWorkerType,
        hcheck_sync_enabled: this.hcheckSyncEnabled,
        worker_types: this.realEstate.worker_types,
        allows_period_appointment: this.realEstate.allows_period_appointment,
        allows_end_time_appointment: this.realEstate.allows_end_time_appointment,
        service_duration_validation_type: this.realEstate.service_duration_validation_type,
        internal_daily_service_limit: this.realEstate.internal_daily_service_limit,
        dispute_creation_days_limit: toDisputeCreationDaysLimitBody(
          this.realEstate.dispute_creation_days_limit,
          this.loadedDisputeCreationDaysLimit
        ),
      }

      if (this.realEstate.usesCustomFeedback)
        realEstate.custom_feedback_url = this.realEstate.feedbackURL
      return realEstate
    },

    buildCreateBody() {
      var realEstate = {
        name: this.realEstate.name,
        cnpj: unmaskText(this.realEstate.cnpj),
        currency: this.realEstate.currency,
        information: this.realEstate.information,
        uses_custom_feedback: this.realEstate.usesCustomFeedback,
        use_cancelation_limit: this.realEstate.use_cancelation_limit,
        cancelation_limit_hours: this.realEstate.cancelation_limit_hours,
        address: this.realEstate.address,
        postal_code: unmaskText(this.realEstate.postalCode),
        phone: this.realEstate.phone,
        appointment_resume_contact: this.realEstate.appointmentResumeContact,
        email: this.realEstate.email,
        user: {
          name: this.newUser.name,
          cpfcnpj: unmaskText(this.newUser.cpf),
          phone: this.newUser.phone,
          email: this.newUser.email,
        },
        isTester: this.realEstate.isTester,
        devolus_token: this.realEstate.devolusToken,
        devolus_id: this.realEstate.devolusId,
        devolus_webhook_url: this.realEstate.devolusWebhookUrl,
        add_default_contract: this.realEstate.addDefaultContract,
        send_to_nomah: this.realEstate.sendToNomah,
        receive_confirmation_mail_copy:
          this.realEstate.receiveConfirmationMailCopy,
        language: this.realEstate.language,
        allows_key_pickup: this.realEstate.allows_key_pickup ?? false,
        partner_id: this.realEstate.partner_id,
        negative_balance_limit: this.realEstate.negativeBalanceLimit ?? 0,
        use_custom_worker_type: this.realEstate.useCustomWorkerType,
        hcheck_sync_enabled: this.hcheckSyncEnabled,
        worker_types: this.realEstate.worker_types,
        allows_period_appointment: this.realEstate.allows_period_appointment,
        allows_end_time_appointment: this.realEstate.allows_end_time_appointment,
        internal_daily_service_limit: this.realEstate.internal_daily_service_limit,
        dispute_creation_days_limit: toDisputeCreationDaysLimitBody(this.realEstate.dispute_creation_days_limit, null),
      }

      if (this.realEstate.usesCustomFeedback) {
        realEstate.custom_feedback_url = this.realEstate.feedbackURL
      }

      if (this.realEstate.addDefaultContract) {
        realEstate.subscription_type = this.realEstate.subscriptionType
        realEstate.services = this.realEstate.services
        realEstate.startDate = this.realEstate.startDate
        realEstate.monthly_capacity = this.realEstate.monthlyCapacity
        realEstate.daily_capacity = this.realEstate.dailyCapacity
        realEstate.overuse_fee = toOveruseFee(this.realEstate.overuseFee)
      }

      return realEstate
    },

    async save() {
      this.setIsLoading(true)

      if (!this.realEstate.id) {
        this.create()
      } else {
        this.update()
      }
    },

    create() {
      this.createCustomer(this.buildCreateBody())
        .then(async () => {
          this.showSuccessMessage(
            this.$t('customer.create.messages.successCreation')
          )
          await this.fetchCustomers()
          this.$refs.createModal.closeCreateModal()
        })
        .catch(({ data }) => {
          if (data.errors) {
            let errorMessage = ''
            data.errors.forEach((error) => {
              errorMessage += `${this.$t('customer.create.' + error.field)}: ${
                error.message
              }; `
            })
            this.showErrorMessage(errorMessage)
          } else {
            this.showErrorMessage(data.message)
          }
        })
        .finally(() => {
          this.setIsLoading(false)
        })
    },

    update() {
      this.updateCustomer(this.buildUpdateBody())
        .then(async () => {
          this.showSuccessMessage(
            this.$t('customer.create.messages.successUpdate')
          )
          await this.fetchCustomers()
          this.$refs.createModal.closeCreateModal()
        })
        .catch(({ data }) => {
          if (data.errors) {
            let errorMessage = ''
            data.errors.forEach((error) => {
              errorMessage += `${this.$t('customer.create.' + error.field)}: ${
                error.message
              }; `
            })
            this.showErrorMessage(errorMessage)
          } else {
            this.showErrorMessage(data.message)
          }
        })
        .finally(() => {
          this.setIsLoading(false)
        })
    },

    resetForm() {
      this.realEstate = {
        country: localStorage.getItem('user_country') ?? 'BR',
        language: localStorage.getItem('user_language') ?? 'PT_BR',
        currency: localStorage.getItem('user_currency') ?? 'BRAZILIAN_REAL',
        partner_id: null,
        logo: null,
        worker_types: [],
        allows_period_appointment: true,
        allows_end_time_appointment: false,
        internal_daily_service_limit: DEFAULT_INTERNAL_DAILY_SERVICE_LIMIT,
        dispute_creation_days_limit: null,
      }

      this.loadedDisputeCreationDaysLimit = null
      this.newUser = {}
      this.partner = null
      this.customerLogo = null
      this.hcheckSyncEnabled = true
    },

    handleCustomerLogo(file) {
      const logoImg = this.$refs.customerLogo
      this.customerLogo = file[0]

      if (this.customerLogo) {
        const reader = new FileReader()
        reader.onload = (event) => (logoImg.src = event.target.result)
        reader.readAsDataURL(this.customerLogo)
      }
    },

    setCustomerLogo() {
      const logoImg = this.$refs.customerLogo

      if (this.customerLogo) {
        if (typeof this.customerLogo === 'string') {
          logoImg.src = this.customerLogo
          return
        }
      }
    },

    removePartner() {
      this.partner = null
      this.realEstate.partner_id = null
    },

    validateWorkerTypes(newValue, oldValue) {
      if (!Array.isArray(newValue)) return

      const hasThirdParty = newValue.includes('THIRD_PARTY')
      const hasHost = newValue.includes('HOST')
      const hasInternal = newValue.includes('INTERNAL')

      const oldArray = Array.isArray(oldValue) ? oldValue : []
      const hadThirdParty = oldArray.includes('THIRD_PARTY')
      const hadHost = oldArray.includes('HOST')
      const hadInternal = oldArray.includes('INTERNAL')

      const thirdPartyJustAdded = hasThirdParty && !hadThirdParty
      const hostOrInternalJustAdded = (hasHost && !hadHost) || (hasInternal && !hadInternal)

      if (thirdPartyJustAdded && (hasHost || hasInternal)) {
        this.realEstate.worker_types = newValue.filter(type => type !== 'HOST' && type !== 'INTERNAL')
      }

      else if (hostOrInternalJustAdded && hasThirdParty) {
        this.realEstate.worker_types = newValue.filter(type => type !== 'THIRD_PARTY')
      }
    },
  },

  watch: {
    'realEstate.worker_types'(newValue, oldValue) {
      this.validateWorkerTypes(newValue, oldValue)
    },

    'realEstate.allows_period_appointment'(newValue) {
      if (newValue === false) {
        this.realEstate.allows_end_time_appointment = false
      }
    },
  },

  mounted() {
    this.fetchPartners()
  },
}
</script>

<style>
.customerLogoWrapper {
  border-radius: 6px;
}

.customerLogoWrapper img {
  border-radius: 3px;
}
</style>
